import { StateService } from '@uirouter/core';
import angular, { ILocationService, IScope } from 'angular';
import { ngStorage } from 'ngstorage';
import { hasOwnProperty, replaceCollection } from '../../tn.common';
import { hasUpgradeOrRenewCartLicenses } from '../../tn.licenses/has-license-type.filter/has-upgrade-or-renew-licenses';
import { licensesToUpgradeFrom } from '../../tn.licenses/has-license-type.filter/licenses-to-upgrade-from';
import { LicenseListService } from '../../tn.licenses/license-list.service/licenseList';
import { LicenseService } from '../../tn.licenses/license.service/license-service';
import { BucketService } from '../../tn.pricing/buckets.service/buckets';
import { UserService } from '../../tn.user/service.tn.user';
import { LocalCarts } from '../local-carts.service/localCarts';
import { CartService } from '../service.tn.cart';
import { PopoverService } from '../../tn.popover/service.tn.popover';

SavedCartDetail.$inject = [
    'cart',
    'localCarts',
    'tn.cart.service',
    '$tnPopover',
    '$state',
    '$scope',
    'tnLicenseListService',
    '$location',
    'tn.user.service',
    'tnLicenseService',
    'tnBuckets',
    '$localStorage',
];

export function SavedCartDetail( // eslint-disable-line max-params
    cart,
    localCarts: LocalCarts,
    cartService: CartService,
    $tnPopover: PopoverService,
    $state: StateService,
    $scope: IScope,
    licenseListService: LicenseListService,
    $location: ILocationService,
    userService: UserService,
    licenseService: LicenseService,
    bucketsService: BucketService,
    $localStorage: ngStorage.StorageService
): void {
    const vm = angular.extend(this, {
        isLoggedIn: false,
        isOwner: false,
        cart: cart,
        originalCart: null,
        newLicenses: [],
        upgradedOrRenewalLicenses: [],

        licensesUpgradeDict: {},
        licenseCosts: {},
        //methods
        showBuyCartPopover: showBuyCartPopover,
        buySavedCart: buySavedCart,
        showRemoveCartPopover: showRemoveCartPopover,
        removeSavedCart: removeSavedCart,
        showEmailPopover: showEmailPopover,
        showSaveCartPopover: showSaveCartPopover,
        removeFontRightsFromCart,
        countLicenseTypes: cartService.countLicenseTypes,

        //popovers
        buyCartPopover: {},
        removeCartPopover: {},

        totals: {},
    });

    initalize();
    function initalize() {
        vm.originalCart = angular.copy(vm.cart);
        vm.buyCartPopover = $tnPopover.popoverXS(
            angular.element('.popover-saved-cart-buy')
        );
        vm.saveCartPopover = $tnPopover.popoverXS(
            angular.element('.popover-save-saved-cart')
        );
        vm.removeCartPopover = $tnPopover.popoverXS(
            angular.element('.popover-saved-cart-remove')
        );
        vm.emailCartPopover = $tnPopover.popoverXS(
            angular.element('.popover-cart-email')
        );
        vm.hasWebLicenseSelfHosted = licenseListService.hasWebLicenseSelfHosted(
            vm.cart.license
        );
        if (userService.isLoggedIn()) {
            const user = userService.getUser();
            vm.isLoggedIn = true;
            vm.isOwner = user.id === cart.user;
        }
        let differentUser = false;
        if (!vm.isOwner) {
            // Checking out a saved cart that belongs to a different user can result in edge cases when the
            // saved cart is an upgrade/renew of style(s) the user may/may NOT have already purchased

            // Ids in the saved cart have ids that belong to the creator user, we should update those ids
            // and make sure they are the ones that belong to the current user.
            differentUser = true;
        }
        _updateLicensesUpgradeDict(differentUser);
        $localStorage.currentSavedCart = vm.cart;
    }

    function showBuyCartPopover($event) {
        vm.buyCartPopover.show($event.currentTarget);
    }

    function buySavedCart() {
        cartService.setCart(vm.cart);
        $state.go('home.checkout.review');
    }

    function showRemoveCartPopover($event) {
        vm.removeCartPopover.show($event.currentTarget);
    }

    function showEmailPopover($event) {
        // Reset state
        const popoverController = angular
            .element('.popover-cart-email')
            .controller();
        popoverController.showEmailForm();

        vm.emailCartPopover.show($event.currentTarget);
    }

    function removeSavedCart() {
        localCarts.deleteCart(vm.cart);
        vm.removeCartPopover.hide();
        $location.path('/account/carts');
    }

    function showSaveCartPopover($event) {
        vm.saveCartPopover.show($event.currentTarget);
    }

    vm.licenseCosts = {};
    vm.acceptedEULA = false;
    vm.editPopover = $tnPopover.popoverXS(
        angular.element('.popover-styles-edit'),
        false
    );
    vm.upgradeRenewPopover = $tnPopover.popoverXS(
        angular.element('.popover-upgrade-renew-licenses'),
        false
    );

    // Update price totals when we change the cart
    $scope.$watch(
        'vm.cart.license',
        function () {
            separateUpgrades(vm.cart.license);
            recalculateTotals();
        },
        true
    );

    $scope.$watchCollection('vm.licensesUpgradeDict', recalculateTotals);

    function recalculateTotals() {
        // Check if has self-hosted
        vm.hasWebLicenseSelfHosted = cartService.hasWebLicenseSelfHosted();

        localCarts
            .recalculateTotals(
                vm.cart,
                vm.totals,
                vm.licensesUpgradeDict,
                vm.licenseCosts
            )
            .then((newTotals) => {
                vm.cart = newTotals['cart'];
                vm.totals = newTotals['cartTotals'];
                vm.licensesUpgradeDict = newTotals['licensesUpgradeDict'];
                vm.licenseCosts = newTotals['licenseCosts'];
            });
    }

    function _updateLicensesUpgradeDict(differentUser = false) {
        if (!vm.isLoggedIn) {
            // User is not logged in, this cart is NOT an upgrade/renewal
            _unsetUpgradeRenewal();
        }
        if (hasUpgradeOrRenewCartLicenses(vm.cart.license)) {
            licenseService.getLicensesFromServer().then((fullLicenses) => {
                const userLicensesUpgrade = {};
                licenseListService
                    .getUpgradeOrRenewalLicenses(vm.cart.license)
                    .forEach((license) => {
                        if (differentUser) {
                            _setUpgradeRenewalIds(fullLicenses, license);
                        }
                        licensesToUpgradeFrom(fullLicenses, vm.cart.license)
                            .filter((originalLicense) => {
                                let id = null;
                                Object.keys(originalLicense.fontRights).forEach(
                                    (rightType) => {
                                        const licenseId =
                                            license.fontRights[rightType].id;
                                        const originalId =
                                            originalLicense.fontRights[
                                                rightType
                                            ].id || null;
                                        id =
                                            licenseId &&
                                            originalId &&
                                            licenseId === originalId;
                                    }
                                );
                                return id;
                            })
                            .forEach((originalLicense) => {
                                if (
                                    !hasOwnProperty(
                                        userLicensesUpgrade,
                                        license.font.id
                                    )
                                ) {
                                    userLicensesUpgrade[license.font.id] = {
                                        style: license.font,
                                        fontRights: {},
                                    };
                                }
                                Object.keys(originalLicense.fontRights).forEach(
                                    (rightType) => {
                                        userLicensesUpgrade[
                                            license.font.id
                                        ].fontRights[rightType] =
                                            originalLicense.fontRights[
                                                rightType
                                            ];
                                    }
                                );
                            });
                    });
                if (Object.keys(userLicensesUpgrade).length === 0) {
                    _unsetUpgradeRenewal();
                } else {
                    angular.copy(userLicensesUpgrade, vm.licensesUpgradeDict);
                }
            });
        } else {
            _unsetUpgradeRenewal();
        }
    }

    function _unsetUpgradeRenewal() {
        // A user that is not logged in should see a renewal/upgrade cart as a new purchase.
        // Same should happen if the user has not previously purchased that style.
        vm.cart.license.forEach((license) => {
            Object.keys(license.fontRights).forEach((rightType) => {
                license.fontRights[rightType]['upgrade'] = false;
                license.fontRights[rightType]['renew'] = false;
            });
        });
        angular.copy({}, vm.licensesUpgradeDict);
    }

    function _setUpgradeRenewalIds(fullLicenses, license) {
        let fullLicenseFontRights = {};
        const fullLicensesSameIds = fullLicenses.filter(
            (fullLicense) => fullLicense.style.id === license.font.id
        );
        if (fullLicensesSameIds.length < 1) {
            // The user has not previously purchased this license. This is NOT an upgrade/renewal.
            Object.keys(license.fontRights).forEach((rightType) => {
                license.fontRights[rightType]['upgrade'] = false;
                license.fontRights[rightType]['renew'] = false;
            });
            _unsetUpgradeRenewal();
            return;
        } else {
            fullLicenseFontRights = fullLicensesSameIds[0].fontRights;
        }
        Object.keys(license.fontRights).forEach((rightType) => {
            if (rightType in fullLicenseFontRights) {
                // The license may already be perpetual and/or the cart may be
                // a downgrade and that should not be allowed
                vm.upgradeRenewalNotAllowed = !_shouldUpgradeRenewalBeAllowed(
                    license.fontRights,
                    fullLicenseFontRights,
                    rightType
                );
                if ('id' in fullLicenseFontRights[rightType]) {
                    license.fontRights[rightType]['id'] =
                        fullLicenseFontRights[rightType]['id'];
                }
            }
        });
    }

    function _shouldUpgradeRenewalBeAllowed(upgradeTo, upgradeFrom, rightType) {
        let perpetual = false;
        let allowUpgradeRenewal = false;
        if (rightType === 'apps') {
            perpetual = upgradeFrom[rightType]['years'] === -1;
            allowUpgradeRenewal =
                upgradeFrom[rightType]['apps'] < upgradeTo[rightType]['apps'] ||
                !perpetual;
        } else if (rightType === 'web') {
            perpetual = upgradeFrom[rightType]['years'] === -1;
            allowUpgradeRenewal =
                upgradeFrom[rightType]['views'] <
                    upgradeTo[rightType]['views'] || !perpetual;
        } else if (rightType === 'epub') {
            allowUpgradeRenewal =
                upgradeFrom[rightType]['ebooks'] <
                upgradeTo[rightType]['ebooks'];
        } else if (rightType === 'desktop') {
            allowUpgradeRenewal =
                upgradeFrom[rightType]['computers'] <
                upgradeTo[rightType]['computers'];
        }
        return allowUpgradeRenewal;
    }

    function removeFontRightsFromCart(licenses, upgradeOrRenew) {
        licenseListService.removeFontRights(
            vm.cart.license,
            licenses,
            upgradeOrRenew
        );

        if (vm.cart.id !== undefined && vm.cart.id !== null) {
            // otherwise, update the cart if there's a remote reference
            cartService.updateCartOnServer(vm.cart);
        }
    }

    function separateUpgrades(licenses) {
        replaceCollection(
            vm.newLicenses,
            licenseListService.getNewLicenses(licenses)
        );
        replaceCollection(
            vm.upgradedOrRenewalLicenses,
            licenseListService.getUpgradeOrRenewalLicenses(licenses)
        );
    }

    vm.showEditPopover = function (event, licenses) {
        bucketsService
            .getIntersectingBuckets(licenses.map((l) => l.font))
            .then((intersectionLicenses) => {
                const updatedLicenses =
                    bucketsService.getClosestAcceptableLicenses(
                        intersectionLicenses,
                        licenses
                    );
                angular
                    .element('.popover-styles-edit')
                    .controller()
                    .initialize(vm.cart, updatedLicenses);
                vm.editPopover.show(event.currentTarget);
            });
    };

    vm.showUpgradeRenewPopover = function (
        event,
        licenses,
        licenseType = null
    ) {
        if (vm.countLicenseTypes(licenses) === 1 || licenseType) {
            if (!licenseType) {
                const fontRights = licenses[0].fontRights;
                Object.keys(fontRights).forEach(function (fontType) {
                    if (
                        ['computers', 'ebooks', 'years'].some(
                            (key) => fontRights[fontType][key]
                        )
                    ) {
                        licenseType = fontType;
                    }
                });
            }
            angular
                .element('.popover-upgrade-renew-licenses')
                .controller()
                .initialize(vm.cart, licenses, [licenseType], 'cart');
            vm.upgradeRenewPopover.show(event.currentTarget);
        }
    };

    userService.login$.subscribe((user) => {
        vm.isLoggedIn = true;
        vm.isOwner = user.id === cart.user;

        if (vm.originalCart !== vm.cart) {
            // User just logged in, load original cart to recalculate everything.
            angular.copy(vm.originalCart, vm.cart);
        }
        initalize();
    });

    userService.logout$.subscribe(() => {
        vm.isLoggedIn = false;
        vm.isOwner = false;
        vm.upgradeRenewalNotAllowed = false;
        $localStorage.currentSavedCart = null;
        _updateLicensesUpgradeDict(!vm.isOwner);
    });
}
